import queryString from 'query-string';

/**
 * URL transform factory for Photon
 *
 * All image URL transform functions should follow this model:
 * - Factory function receives the original image src
 *    and a configuration for the particular position in which this image will appear (sources, sizes, global transforms)
 * - Factory returns a function that takes a series of transformation parameters (width, height, type of transform, etc.)
 * - Returned function will generate a new URL based on those parameters
 *
 * @see 'src/config/images/index.js'
 * @param {object} positionConfig - configuration options for an image position (called an image size in WordPress)
 */
export default function createTransformSourcePhoton(positionConfig) {
  /**
   * URL transform function for Photon
   *
   * @param {string} originalSource - original source URL for an image
   * @param {Object} params - transform parameters to be used for modifying original image URL
   * @param {number} params.width - target width value for image transformation
   * @param {number} params.height - target height value for image transformation
   * @param {string} params.transform - transformation type for image ('fit', 'resize')
   * @param {number} params.quality - target image compression quality
   */
  return (originalSource, params) => {
    // Return original image if width or height aren't set
    if (!params.width) {
      return originalSource;
    }

    const cleanSource = originalSource.split('?')[0];
    const newParams = {
      transform: params.transform || positionConfig.transform || 'fit',
      quality: params.quality || positionConfig.quality,
      height: params.height || 9999, // set height to an arbitrarily high value (auto height) if none specified
      ...params,
    };
    const { width, height, transform, quality } = newParams;
    let transformParams = {};

    // Set params for provided transform
    switch (transform) {
      case 'crop':
        transformParams = { crop: `0,0,${width}px,${height}px` };
        break;

      case 'fit':
      case 'resize':
        transformParams = { [transform]: `${width},${height}` };
        break;

      case 'none':
      default:
        transformParams = {
          w: width,
          h: height,
        };
        break;
    }

    const photonParams = queryString.stringify({
      quality,
      ...transformParams,
    });

    return `${cleanSource}?${photonParams}`;
  };
}
